/**
 * クライアントイベント計測（Google Analytics 4 / gtag.js）。
 *
 * なぜプラットフォームの計測系ではなく GA4 なのか:
 *   認証に到達しなかった起動はプラットフォーム側からは見えない
 *   （`/arcade/events` はユーザーセッション、`/sdk/errors` は SDK トークンが要る）。
 *   「広告をタップした」から「YMG SDK 初期化完了」までの間こそ離脱が起きる区間で、
 *   gtag は最初のスクリプトから動いて認証前・認証中・認証後のすべてを記録できる。
 *   プラットフォームのガイド
 *   (yamanekogames docs/35_Guides/12d_Analytics_Integration.md) が指定する
 *   送信先でもある（全イベントに `game_id` を付ける / GA4 プロパティを
 *   `/dev-tools/analytics/{gameId}` が読む）。
 *
 * `VITE_GA4_MEASUREMENT_ID` が空のビルドでは完全に no-op（gtag.js も読み込まない）。
 * Dev / Staging は既定で空。
 *
 * プライバシー: ユーザー ID・LINE プロフィール・表示名・招待コードの値・
 * 自由入力テキストは送らない。パラメータは粗いカテゴリ値と数値のみ。
 */

import { INVITE_PARAM_KEY } from '../config/invite'
import { getPlatformId, isCapacitorNative } from '../utils/platform'
import { getGameId } from './sdk'
import { parseHashQuery, parseLiffStateQuery } from '../utils/liffState'

const MEASUREMENT_ID = (import.meta.env.VITE_GA4_MEASUREMENT_ID || '').trim()
const BUILD_ENV = import.meta.env.MODE
const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js'

/** ネイティブ WebView で自前管理する GA client_id の保存先キー。 */
const CLIENT_ID_STORAGE_KEY = 'ga4_client_id'

/** 広告経由の着地に広告ネットワークが付与するクリック ID のクエリキー。 */
const AD_CLICK_PARAM_KEY = 'amoadcid'

/** `reason` の最大長。例外メッセージがそのまま長大化するのを防ぐ。 */
const REASON_MAX_LENGTH = 100

type GtagParams = Record<string, string | number | boolean>

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let initialized = false

/**
 * LIFF が決着するまでプラットフォーム判定は当てにならない
 * （`getPlatformId()` は LIFF 未初期化を `web_guest` と読む）。
 * 決着前のイベントは `platform_id: 'unknown'` で送る。
 */
let platformResolved = false

/** このビルドに測定 ID が設定されているか。 */
export function isAnalyticsEnabled(): boolean {
  return MEASUREMENT_ID.length > 0
}

/**
 * ネイティブ WebView 用に永続化した client_id を返す（無ければ生成して保存する）。
 *
 * Capacitor の WebView は Cookie の寿命が OS / WebView の都合に左右され、
 * GA が `_ga` を保てないと起動のたびに別ユーザーとして数えられる。
 * GA の client_id 形式（`<乱数>.<UNIX 秒>`）を自前で作り、localStorage に固定する。
 *
 * Web（LINE ミニアプリ）では従来どおり GA に Cookie を管理させるため呼ばない。
 */
function resolveNativeClientId(): string | null {
  try {
    const stored = window.localStorage.getItem(CLIENT_ID_STORAGE_KEY)
    if (stored) return stored
    const random = Math.floor(Math.random() * 9_000_000_000) + 1_000_000_000
    const generated = `${random}.${Math.floor(Date.now() / 1000)}`
    window.localStorage.setItem(CLIENT_ID_STORAGE_KEY, generated)
    return generated
  } catch (err) {
    // fallback:ok — localStorage が使えない端末では client_id を指定せず GA の既定
    // （Cookie）に任せる。計測はこの起動だけ新規ユーザー扱いになるが、ゲーム進行と
    // 他イベントの送信は妨げない。握り潰さず理由をログに残す。
    console.warn('[analytics] client_id persistence unavailable:', err)
    return null
  }
}

/**
 * gtag.js を読み込み、プロパティを設定する。
 * スクリプト到着前に呼んでも安全（gtag は `dataLayer` にキューイングする）。
 * 単一ページアプリなので `send_page_view` は切り、`app_launch` を起動計測に使う。
 */
export function initAnalytics(): void {
  if (!isAnalyticsEnabled() || initialized) return
  initialized = true

  // gtag stamps page_location on every hit from the live URL, which on the LINE login
  // return page still carries ?code=…&state=… (and invite codes). Pin it to origin+pathname.
  const config: GtagParams = { send_page_view: false, page_location: `${window.location.origin}${window.location.pathname}` }
  if (isCapacitorNative()) {
    const clientId = resolveNativeClientId()
    if (clientId) {
      // GA は渡された client_id を優先して使う（Cookie が消えても localStorage 側の値で継続）。
      // ※ `client_storage` は現行 GA4 gtag.js に無いパラメータなので指定しない。
      config.client_id = clientId
    }
  }

  window.dataLayer = window.dataLayer ?? []
  window.gtag = function gtag() {
    // gtag は配列ではなく `arguments` オブジェクトを読む。展開して push すると壊れる。
    window.dataLayer!.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', MEASUREMENT_ID, config)

  const script = document.createElement('script')
  script.async = true
  script.src = `${GTAG_SRC}?id=${encodeURIComponent(MEASUREMENT_ID)}`
  document.head.appendChild(script)
}

/** LIFF 初期化が決着した直後に呼ぶ。以降 `platform_id` は実判定値になる。 */
export function markAnalyticsPlatformResolved(): void {
  platformResolved = true
}

/**
 * イベントを 1 件送る。
 *
 * `game_id` は全イベント必須（プラットフォームガイド §4.2）。
 * `platform_id` / `build_env` / `app_version` を添えることで、
 * 1 プロパティに複数環境・複数ビルドが同居しても混ざらない。
 *
 * gtag 未到着時の no-op は仕様（測定 ID 未設定ビルドでは外部スクリプトを
 * 一切読み込まないため `window.gtag` が存在しない）。
 */
export function trackEvent(name: string, params: GtagParams = {}): void {
  if (!isAnalyticsEnabled() || !window.gtag) return
  window.gtag('event', name, {
    game_id: getGameId(),
    platform_id: platformResolved ? getPlatformId() : 'unknown',
    build_env: BUILD_ENV,
    app_version: __APP_VERSION__,
    ...params,
  })
}

/**
 * このページロードが何を伴って来たかを URL から読む。
 *
 * - `amoadcid` は広告の着地 URL に付くクリック ID。
 * - `ymg_invite` はフレンド招待コード（config/invite.ts）。値自体は送らない。
 * - `transit`: `liff.state` 付きのロードはパーマネントリンク経由コールドスタートの
 *   中継ページ。`liff.init()` が成功するとページは実 URL に差し替わる（クエリは
 *   URL 直下に移る）ため、中継ロードを起動として数えてはいけない。init が失敗した
 *   場合はページが残るので、フラグは `liff.state` の中からも読んで `liff_*` に付ける。
 */
export function readLaunchContext(
  search: string,
  hash: string = typeof window === 'undefined' ? '' : window.location.hash,
): {
  transit: boolean
  ad_click: boolean
  has_invite: boolean
} {
  const query = new URLSearchParams(search)
  const liffState = query.get('liff.state')
  // liff.state / hash の解釈は social.ts の招待コード抽出と同じヘルパーを使う
  // (二重エンコードされた liff.state、hash router 形式の両方を拾う)。
  const nested = liffState === null ? new URLSearchParams() : parseLiffStateQuery(liffState)
  const inHash = parseHashQuery(hash)
  const has = (key: string) => query.has(key) || nested.has(key) || inHash.has(key)
  return {
    transit: liffState !== null,
    ad_click: has(AD_CLICK_PARAM_KEY),
    has_invite: has(INVITE_PARAM_KEY),
  }
}

/**
 * このページロードの起動を記録する。LIFF の中継ページでは送らない。
 *
 * 既知の二重計上: 外部ブラウザ起動が LINE ログインを挟むと同じ URL に戻って
 * もう一度記録される。`liff_login_redirect` がその往復を数えるので差し引ける。
 */
export function trackAppLaunch(search: string = window.location.search): void {
  const { transit, ...params } = readLaunchContext(search)
  if (transit) return
  trackEvent('app_launch', params)
}

/**
 * `initializeLiff()` の結果を記録する。起動フラグを添えるので、
 * `app_launch` に到達しない中継ページでの失敗でも広告経由かどうかが分かる。
 *
 * - `ready`       → `liff_ready`
 * - `redirecting` → `liff_login_redirect`（外部ブラウザのログイン往復。失敗ではない）
 * - `unavailable` → `liff_failed`
 * - `skipped`     → Capacitor ネイティブ。LIFF を使わないので何も送らない。
 */
export function trackLiffOutcome(
  result: { status: string; error?: string },
  search: string = window.location.search,
): void {
  if (result.status === 'skipped') return
  const name =
    result.status === 'redirecting'
      ? 'liff_login_redirect'
      : result.status === 'ready'
        ? 'liff_ready'
        : 'liff_failed'
  trackEvent(name, {
    ...readLaunchContext(search),
    status: result.status,
    reason: describeReason(result.error),
  })
}

/** YMG SDK 初期化の結果。`error` が null / undefined なら成功。 */
export function trackSdkOutcome(error: unknown): void {
  trackEvent(error ? 'sdk_failed' : 'sdk_ready', { reason: describeReason(error) })
}

/** `placement` でリワード広告が要求され、`watched` が報酬獲得に至ったか。 */
export function trackAdReward(placement: string, watched: boolean): void {
  trackEvent('ad_reward', { placement, watched })
}

/** エラーを短い文字列に落とす。自由入力ではないが長さは切り詰める。 */
function describeReason(error: unknown): string {
  if (error === null || error === undefined) return ''
  const message = error instanceof Error ? error.message : String(error)
  return message.slice(0, REASON_MAX_LENGTH)
}
